import property from 'lodash/fp/property';
import isEmpty from 'lodash/isEmpty';

import { Dispatch, GetFrontOfficeState } from '@lumapps/redux/types';
import { CONTENT_FILTER_SUPPORTED_WIDGETS, WIDGET_CONTENT_FILTER_TYPE } from '@lumapps/widget-content-filter/constants';

import { FetchBlockParams, getWidgetBlocks, getWidgetFilterProperties } from '../../api';
import { formatWidgetFiltersForApi } from '../../utils/formatWidgetFiltersForApi';
import {
    getClientComputedWidgetsOfType,
    getMainWidget,
    getWidget,
    isNGIContentFiltersFFEnabled as isNGIContentFiltersFFEnabledSelector,
} from '../selectors';
import { actions } from '../slice';
import { fetchWidgetBlocks } from './fetchWidgetBlocks';

export const fetchWidgetBlocksById =
    ({
        params,
        widgetId,
        widgetType,
        isMainWidget,
    }: {
        params?: FetchBlockParams;
        widgetId: string;
        widgetType: string;
        isMainWidget: boolean;
    }) =>
    async (dispatch: Dispatch, getState: GetFrontOfficeState) => {
        const currentState = getState();
        const rawFilters = property('filters', getWidget(currentState, { widgetId }));
        const filtersFromStore = rawFilters ? formatWidgetFiltersForApi(widgetType, rawFilters) : undefined;
        const filtersFromParams = params?.filters ? formatWidgetFiltersForApi(widgetType, params?.filters) : undefined;

        const contentFilterWidgets = getClientComputedWidgetsOfType(WIDGET_CONTENT_FILTER_TYPE)(currentState);
        const isNGIContentFiltersFFEnabled = isNGIContentFiltersFFEnabledSelector(currentState);
        const filterPropertiesAlreadyFetched = Boolean(getMainWidget(currentState)?.filterProperties);

        /** Filters from params must take precedence over those from the store */
        const filters = { ...filtersFromStore, ...filtersFromParams };
        /** Forcing block no result if we are filtering a main widget with a content filter */
        const fetchParams = { ...params, filters, forceDisplay: isMainWidget && !isEmpty(contentFilterWidgets) };

        dispatch(
            fetchWidgetBlocks(
                widgetId,
                async (acceptLanguageHeader) => {
                    return getWidgetBlocks(widgetType, widgetId, fetchParams, acceptLanguageHeader);
                },
                fetchParams,
                isMainWidget,
            ),
        );

        const shouldFetchProperties =
            !isEmpty(contentFilterWidgets) &&
            isNGIContentFiltersFFEnabled &&
            isMainWidget &&
            !filterPropertiesAlreadyFetched &&
            CONTENT_FILTER_SUPPORTED_WIDGETS.includes(widgetType);

        // If we have some content filter widgets on the page, we need to fetch properties from the main widget.
        if (shouldFetchProperties) {
            try {
                const filterProperties = await getWidgetFilterProperties(widgetType, widgetId, params);
                dispatch(actions.setWidgetProperties({ widgetId, widgetProperties: { filterProperties } }));
            } catch (error) {
                // If we cannot get the properties from the main widget, then the content filters can't work.
                // So we put them in error state.
                contentFilterWidgets?.forEach((contentFilter) => {
                    dispatch(
                        actions.setWidgetProperties({
                            widgetId: contentFilter.widgetId,
                            widgetProperties: {
                                state: 'error',
                            },
                        }),
                    );
                });
            }
        }
    };
